/**
 * calendar/feed.js — Event feed, colouring, weekend-splitting, and type filtering.
 *
 * Owns everything about turning the server's authoritative event feed into rendered
 * FullCalendar events:
 *   - colourizeEvent: class each event by its kind of work (visit-kind-*, painted by
 *     calendar.css) on the event DATA, so it renders correctly on first paint.
 *   - splitWorkingDayEvent / expandEvents: split multi-day all-day events so no block spans
 *     a weekend, all segments sharing the original id.
 *   - filterEventsByType: show/hide by type; in month view render per-day count badges.
 *   - applyFreshEvents / loadEventsForRange / repaintCalendarEvents: the single, token-guarded
 *     event loading path. Fetch -> colourize -> removeAllEvents -> re-add, dropping any stale
 *     out-of-order response, so overlapping view-change fetches can't double the events.
 *
 * Imports only `state` + `toLocalDateStr`, so it has no cycle with the feature modules.
 */
import { state, toLocalDateStr } from './state.js';

// The four visit types' hues, for the month view's per-day count badges (filterEventsByType),
// which match the type filter's dots. Read from the visit-kind roles (tokens/semantic.css),
// which own them; the chips themselves are coloured by class (calendar.css .visit-kind-*).
const rootStyle = getComputedStyle(document.documentElement);
const eventTypeColors = {
    'quote': rootStyle.getPropertyValue('--visit-kind-quote').trim() || '#ffc107',
    'job': rootStyle.getPropertyValue('--visit-kind-job').trim() || '#198754',
    'meeting': rootStyle.getPropertyValue('--visit-kind-meeting').trim() || '#c2255c',
    'other': rootStyle.getPropertyValue('--visit-kind-other').trim() || '#6c757d'
};

/**
 * Pick a legible text colour (black/white) for a background using the YIQ luminance
 * formula — adaptive contrast so light chips (e.g. amber quotes) keep dark text and darker
 * chips keep white, regardless of the entity colours a business configures. Threshold is
 * biased slightly above the 128 midpoint so borderline mid-tones favour more-readable dark.
 */
function contrastText(bgColor) {
    let hex = (bgColor || '').trim().replace('#', '');
    if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
    if (hex.length !== 6) return '#ffffff';
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 140 ? '#000000' : '#ffffff';
}

// Currency symbol for event detail lines — read fresh from #calendar each call so a Turbo
// navigation that swaps the element still resolves the current tenant's symbol.
export function currencySymbol() {
    return document.getElementById('calendar')?.dataset.currencySymbol || '$';
}

/**
 * Whether an event's date has already passed (its day is before today, local time).
 * Date-based, not time-based, so an appointment earlier *today* is not treated as past —
 * matching the user's "past the date" framing. The chip keeps its entity colour but is
 * dimmed via the fc-event-past class so finished work recedes on the schedule.
 */
function isPastEvent(ev) {
    const startRaw = typeof ev.start === 'string' ? ev.start : (ev.start && ev.start.toISOString());
    if (!startRaw) return false;
    return startRaw.substring(0, 10) < toLocalDateStr(new Date());
}

/**
 * Class a server event by the kind of work it is before FullCalendar renders it:
 * visit-kind-<kind> (extendedProps.kind, CalendarEvent.work_kind), which calendar.css
 * paints. On the event DATA, so it paints correctly on the first render. A provisional
 * chip from the Unscheduled panel has no kind yet and takes its type's until the feed
 * reloads. A visit the server marked elevated (extendedProps.priority_label) also gets
 * its fc-priority-* edge class.
 */
export function colourizeEvent(ev) {
    const props = ev.extendedProps || {};
    const kind = props.kind || props.type || 'other';
    const classNames = ['visit-kind', 'visit-kind-' + kind];
    if (isPastEvent(ev)) classNames.push('fc-event-past');
    if (props.priority_label) classNames.push('fc-priority-' + props.priority);
    return Object.assign({}, ev, { classNames });
}

/**
 * Split an all-day event dict into contiguous working-day segments so no block spans a
 * Saturday or Sunday. Non-all-day events and those fully within a working week pass through
 * unchanged. All segments keep the original id so delete/reschedule still work.
 */
function splitWorkingDayEvent(ev) {
    if (!ev.allDay) return [ev];

    const startRaw = typeof ev.start === 'string' ? ev.start : ev.start.toISOString();
    const endRaw = ev.end ? (typeof ev.end === 'string' ? ev.end : ev.end.toISOString()) : null;
    if (!endRaw) return [ev];

    const startDay = new Date(startRaw.substring(0, 10) + 'T00:00:00');
    const endDay = new Date(endRaw.substring(0, 10) + 'T00:00:00');

    // Single-day or no span — nothing to split
    if (endDay <= startDay) return [ev];

    // Check whether any weekend falls inside [startDay, endDay)
    let hasWeekend = false;
    const probe = new Date(startDay);
    while (probe < endDay) {
        if (probe.getDay() === 0 || probe.getDay() === 6) { hasWeekend = true; break; }
        probe.setDate(probe.getDate() + 1);
    }
    if (!hasWeekend) return [ev];

    // Build contiguous working-day segments
    const segments = [];
    let segStart = null;
    const cur = new Date(startDay);
    while (cur < endDay) {
        const dow = cur.getDay();
        if (dow !== 0 && dow !== 6) {
            if (!segStart) segStart = new Date(cur);
        } else {
            if (segStart) {
                segments.push({ s: new Date(segStart), e: new Date(cur) });
                segStart = null;
            }
        }
        cur.setDate(cur.getDate() + 1);
    }
    if (segStart) segments.push({ s: new Date(segStart), e: new Date(endDay) });

    if (segments.length === 0) return [ev];

    return segments.map(seg => ({
        ...ev,
        start: toLocalDateStr(seg.s),
        end: toLocalDateStr(seg.e)
    }));
}

export function expandEvents(events) {
    const out = [];
    for (const ev of events) out.push(...splitWorkingDayEvent(ev));
    return out;
}

/**
 * Load the server feed for a date range, colour + weekend-split it, and render it as the
 * calendar's COMPLETE event set — replacing whatever was there. This is the single event
 * loading path; the calendar has no FullCalendar feed source (see core.js datesSet).
 *
 * A monotonic token guards the duplication race. Switching views fires overlapping fetches,
 * and a function source resolves them with no ordering guarantee: an older response landing
 * after a newer one piles a second copy of every event onto the canvas (the week->month->week
 * doubling). We stamp each load and drop any response that is no longer the latest, so exactly
 * one batch ever reaches the canvas. removeAllEvents() before re-adding means no accumulation.
 */
let activeLoadToken = 0;

async function applyFreshEvents(startStr, endStr) {
    const cal = state.calendar;
    if (!cal) return;
    const token = ++activeLoadToken;

    let events;
    try {
        let url = state.feedUrl
            + '?start=' + encodeURIComponent(startStr)
            + '&end=' + encodeURIComponent(endStr);
        // Crew filtering happens server-side: 'me' resolves to the signed-in
        // user there, so nobody can read another tech's day by editing a URL.
        if (state.assignee) url += '&assignee=' + encodeURIComponent(state.assignee);
        const response = await fetch(url);
        if (!response.ok) throw response.status;
        const data = await response.json();
        events = expandEvents(data.map(colourizeEvent));
    } catch (err) {
        console.error('Calendar feed error:', err);
        return;  // leave the current events in place on failure
    }

    // A newer load started while this fetch was in flight — discard this stale batch so it
    // can't double the events that the newer load is about to (or already did) render.
    if (token !== activeLoadToken) return;

    cal.removeAllEvents();
    events.forEach(ev => cal.addEvent(ev));
    filterEventsByType();
}

/**
 * Load events for a freshly-set date range / view. Called from core.js's datesSet so every
 * view change and prev/next pulls exactly one fresh, deduped, coloured batch.
 */
export function loadEventsForRange(startStr, endStr) {
    applyFreshEvents(startStr, endStr);
}

/**
 * Reload the currently-visible range from the feed (the SSOT) after a create or reschedule,
 * so a newly-written event appears without a manual refresh. Shares the same token guard.
 */
export function repaintCalendarEvents() {
    const cal = state.calendar;
    if (!cal) return;
    cal.unselect();  // drop any active selection mirror before re-rendering
    applyFreshEvents(cal.view.activeStart.toISOString(), cal.view.activeEnd.toISOString());
}

export function filterEventsByType() {
    const calendar = state.calendar;
    if (!calendar) return;

    const selected = document.querySelector('.event-type-radio:checked').value;
    const isMonthView = calendar.view.type === 'dayGridMonth';

    // Show/hide individual events
    // Use 'block' (not 'auto') to un-hide: 'auto' lets a TIMED event fall back to month-view
    // dot/list-item style (plain text, no chip background), which is the "lost colour" bug.
    // 'block' keeps the coloured chip in every view, matching the global eventDisplay.
    const allEvents = calendar.getEvents();
    allEvents.forEach(event => {
        const eventType = event.extendedProps.type || 'other';
        if (selected === 'all') {
            event.setProp('display', 'block');
        } else {
            event.setProp('display', eventType === selected ? 'block' : 'none');
        }
    });

    // Clear any existing count badges
    document.querySelectorAll('.day-event-counts').forEach(el => el.remove());

    // In month view with a specific type selected, show count badges and hide event blocks
    if (isMonthView && selected !== 'all') {
        // Hide individual event blocks in month view when filtered
        allEvents.forEach(event => {
            event.setProp('display', 'none');
        });

        // Count events per day for the selected type
        const countsByDate = {};
        allEvents.forEach(event => {
            const eventType = event.extendedProps.type || 'other';
            if (eventType !== selected) return;
            const dateKey = toLocalDateStr(event.start);
            countsByDate[dateKey] = (countsByDate[dateKey] || 0) + 1;
        });

        // Inject count badges into day cells
        const color = eventTypeColors[selected] || '#6c757d';
        const textColor = contrastText(color);
        Object.entries(countsByDate).forEach(([dateKey, count]) => {
            const cell = document.querySelector(`[data-date="${dateKey}"] .fc-daygrid-day-events`);
            if (cell) {
                const badge = document.createElement('div');
                badge.className = 'day-event-counts';
                badge.innerHTML = `<span class="day-event-count" style="background-color:${color}; color:${textColor}">${count}</span>`;
                cell.appendChild(badge);
            }
        });
    }
}
